import React, { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import { Box } from "@mui/material";
import FilterCard from "./FilterCard";

const Cards = ({
  width,
  data = [],
  title,
  subtitle,
  onFilterChange,
  flexwrap,
  /** When true, keeps all cards on one horizontal row (equal flex columns). */
  singleRow,
  setSelectedMonth,
  setSelectedYear,
  selectedMonth,
  selectedYear,
}) => {
  const [selectedFilters, setSelectedFilters] = useState({});
  /** Mirrors `selectedFilters` for reliable compare/update in `handleSelect` (avoids stale closure). */
  const selectedFiltersRef = useRef({});
  /** Last parent-driven `defaultValue` per option key — when it changes (e.g. after save), sync dropdown local state. */
  const lastParentDefaultByKey = useRef({});

  useEffect(() => {
    selectedFiltersRef.current = selectedFilters;
  }, [selectedFilters]);

  /** Stable key for filter rows so we sync when options/defaults change without requiring a memoized `data` reference. */
  const cardFilterSyncKey = JSON.stringify(
    (data || []).map((item) => {
      if (item?.filter === "option") {
        return {
          key: item.key,
          opts: item.filterValues,
          def: item.defaultValue,
        };
      }
      if (item?.filter === "date") {
        return { key: item.key, filter: "date" };
      }
      return { key: item?.key };
    })
  );

  /** Sync option/date card filters when `data` arrives or updates (e.g. API-loaded dropdown options on first paint). */
  useEffect(() => {
    if (!data || data.length === 0) return;
    setSelectedFilters((prev) => {
      const next = { ...prev };
      let changed = false;
      data.forEach((item) => {
        if (item?.filter === "option" && item?.filterValues?.length > 0) {
          const opts = item.filterValues;
          const optStrings = opts.map(String);
          const d =
            item.defaultValue !== undefined && item.defaultValue !== ""
              ? String(item.defaultValue).trim()
              : "";
          const fallback =
            d !== "" && optStrings.includes(d) ? d : opts[0];
          const cur = prev[item.key];
          const prevParentDef = lastParentDefaultByKey.current[item.key];

          /** Parent `defaultValue` changed (e.g. new row saved) — follow it if still a valid option. */
          if (
            d !== "" &&
            optStrings.includes(d) &&
            prevParentDef !== undefined &&
            prevParentDef !== d &&
            String(cur ?? "") !== d
          ) {
            next[item.key] = d;
            changed = true;
          } else if (
            cur === undefined ||
            cur === "" ||
            !optStrings.includes(String(cur))
          ) {
            next[item.key] = fallback;
            changed = true;
          }
          lastParentDefaultByKey.current[item.key] = d;
        } else if (item?.filter === "date" && next[item.key] === undefined) {
          next[item.key] = dayjs().format("YYYY-MM");
          changed = true;
        }
      });
      const out = changed ? next : prev;
      if (changed) {
        selectedFiltersRef.current = out;
      }
      return out;
    });
  }, [cardFilterSyncKey]);

  /** Keep local + parent in sync; ref avoids stale reads vs `selectedFilters` during rapid changes. */
  const handleSelect = (key, value, filterType) => {
    const prevVal = selectedFiltersRef.current[key];
    if (String(prevVal ?? "") === String(value ?? "")) return;
    const next = { ...selectedFiltersRef.current, [key]: value };
    selectedFiltersRef.current = next;
    setSelectedFilters(next);
    onFilterChange?.(key, value, filterType);
  };


  return (
    <>
      <p
        style={{
          fontWeight: "700",
          fontSize: "22px",
          lineHeight: "150%",
          color: "#001632",
          fontFamily: "Inter",
          textDecoration: "underline",
          marginTop: "1rem",
          marginBottom: "1.5rem",
        }}
      >
        {title}
      </p>
      {subtitle && (
        <p
          style={{
            fontWeight: "700",
            fontSize: "22px",
            lineHeight: "150%",
            color: "#001632",
            fontFamily: "Inter",
            textDecoration: "underline",
            marginTop: "1.3rem",
            marginBottom: "1rem",
          }}
        >
          {subtitle}
        </p>
      )}
      <Box
        sx={{
          display: "flex",
          flexWrap: singleRow ? "nowrap" : flexwrap === "no" ? "nowrap" : "wrap",
          gap: "1rem",
          justifyContent: singleRow ? "flex-start" : "space-between",
          ...(singleRow && {
            overflowX: "auto",
            width: "100%",
          }),
        }}
      >
        {data.map((item, idx) => (
          <FilterCard
            key={idx}
            {...{
              setSelectedMonth,
              setSelectedYear,
              selectedMonth,
              selectedYear,
              width,
              singleRow,
            }}
            item={item}
            selectedValue={selectedFilters?.[item.key] || ""}
            handleSelect={handleSelect}
          />
        ))}
      </Box>
    </>
  );
};

export default Cards;
